/**
 * Stylist Studio — /stylist-studio
 *
 * The stylist's home base. Three states, all served by GET /stylists/me:
 *   • applied=false            → application form
 *   • pending / rejected       → status card + edit-and-resubmit form
 *   • approved                 → "Dashboard shell" with Profile tab (editable)
 *                                and placeholders for Portfolio / Bookings /
 *                                Earnings that later batches will fill in.
 *
 * Cover photo upload uses POST /stylists/me/cover.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, Sparkles, ShieldCheck, CircleAlert, Image as ImageIcon,
  ArrowRight, ExternalLink, ArrowLeft, Lock,
} from "lucide-react";
import {
  SPECIALTY_KEYS, SPECIALTY_LABELS, PRICE_TIERS, TARGET_GENDERS, prettySpecialty,
  VerificationBadge,
} from "@/pages/stylists/_shared";
import { PortfolioTab, ServicesTab, BookingsInboxTab } from "@/pages/stylists/StudioTabs";

const EMPTY = {
  display_name: "",
  headline: "",
  bio: "",
  specialties: [],
  experience_years: 0,
  languages: [],
  city: "",
  target_gender: "all",
  price_tier: "premium",
  hourly_rate_inr: "",
  instagram: "",
  portfolio_url: "",
};

export default function StylistStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/stylists/me").then(({ data }) => {
      setApplied(!!data.applied);
      if (data.profile) {
        setProfile(data.profile);
        setForm(profileToForm(data.profile));
      }
    }).catch(() => { /* signed out state handled below */ })
      .finally(() => setLoading(false));
  }, [user]);

  const isApproved = profile?.verification_status === "approved";
  const isPending = profile?.verification_status === "pending";
  const isRejected = profile?.verification_status === "rejected";
  const isSuspended = profile?.verification_status === "suspended";

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (s) => setF("specialties",
    form.specialties.includes(s)
      ? form.specialties.filter((x) => x !== s)
      : (form.specialties.length >= 8 ? form.specialties : [...form.specialties, s]),
  );

  const submitApply = async (e) => {
    e?.preventDefault?.();
    if (!validate(form)) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        experience_years: Number(form.experience_years) || 0,
        hourly_rate_inr: form.hourly_rate_inr ? Number(form.hourly_rate_inr) : null,
        languages: (typeof form.languages === "string"
          ? form.languages.split(",").map((s) => s.trim()).filter(Boolean)
          : form.languages),
      };
      const { data } = await api.post("/stylists/apply", payload);
      setProfile(data.profile);
      setApplied(true);
      toast.success("Application submitted — we'll review it within 24 hours.");
    } catch (e) {
      toast.error(errMsg(e, "Application failed"));
    } finally { setSaving(false); }
  };

  const submitEdit = async (e) => {
    e?.preventDefault?.();
    if (!validate(form)) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        experience_years: Number(form.experience_years) || 0,
        hourly_rate_inr: form.hourly_rate_inr ? Number(form.hourly_rate_inr) : null,
        languages: (typeof form.languages === "string"
          ? form.languages.split(",").map((s) => s.trim()).filter(Boolean)
          : form.languages),
      };
      const { data } = await api.patch("/stylists/me", payload);
      setProfile(data.profile);
      toast.success("Profile updated.");
    } catch (e) {
      toast.error(errMsg(e, "Update failed"));
    } finally { setSaving(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("cover", file);
      const { data } = await api.post("/stylists/me/cover", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, cover_url: data.cover_url }));
      toast.success("Cover updated.");
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
    finally { setUploadingCover(false); }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <Lock size={32} className="mx-auto text-muted-foreground" />
        <h1 className="editorial-h2 mt-4">Sign in first</h1>
        <p className="text-sm text-muted-foreground mt-3">
          You need a Stylebay account to open a stylist studio.
        </p>
        <Button
          onClick={() => navigate("/login")}
          className="rounded-none mt-6 h-11 uppercase tracking-[0.2em] text-[10px]"
        >
          Sign in
        </Button>
      </div>
    );
  }

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10 pb-24" data-testid="stylist-studio">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-2">
            <Sparkles size={12} /> Stylebay · Studio
          </p>
          <h1 className="editorial-h1 mt-3">Your atelier.</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">
            Manage your public profile, verify your credentials, and get discovered by
            thousands of Stylebay users searching for a real stylist.
          </p>
        </div>
        {isApproved && (
          <Link to={`/stylists/${profile.id}`}>
            <Button variant="outline" data-testid="studio-view-public-btn" className="rounded-none h-10 uppercase tracking-[0.2em] text-[10px] gap-2">
              View public profile <ExternalLink size={12} />
            </Button>
          </Link>
        )}
      </div>

      {/* Status banner */}
      {applied && (
        <div className="mt-8" data-testid={`studio-status-${profile.verification_status}`}>
          {isPending && (
            <div className="border border-amber-500/40 bg-amber-500/5 p-5 flex gap-4 items-start">
              <Loader2 className="animate-spin text-amber-600 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium">Under review</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Our team is verifying your application. You'll get an email within 24 hours.
                  You can keep editing your profile below — updates are saved instantly.
                </p>
              </div>
            </div>
          )}
          {isApproved && (
            <div className="border border-accent/40 bg-accent/5 p-5 flex gap-4 items-start">
              <ShieldCheck className="text-accent shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium">Verified stylist</p>
                <p className="text-xs text-muted-foreground mt-1">
                  You're live on the marketplace. Portfolio uploads and booking come
                  in the next few batches — for now, keep your profile sharp.
                </p>
              </div>
            </div>
          )}
          {isRejected && (
            <div className="border border-destructive/40 bg-destructive/5 p-5 flex gap-4 items-start">
              <CircleAlert className="text-destructive shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium">Application not approved</p>
                {profile.rejection_reason && (
                  <p className="text-xs text-muted-foreground mt-1"><b>Reason:</b> {profile.rejection_reason}</p>
                )}
                <p className="text-xs text-muted-foreground mt-2">Update the details below and re-submit.</p>
              </div>
            </div>
          )}
          {isSuspended && (
            <div className="border border-destructive/40 bg-destructive/5 p-5 flex gap-4 items-start">
              <CircleAlert className="text-destructive shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium">Account suspended</p>
                <p className="text-xs text-muted-foreground mt-1">Contact contact@stylebay.co.in to appeal.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cover upload — only when applied */}
      {applied && (
        <div className="mt-8">
          <p className="eyebrow mb-3">Cover photo</p>
          <label className="block cursor-pointer border border-dashed border-border aspect-[21/9] bg-muted overflow-hidden relative group"
            data-testid="studio-cover-upload">
            {profile?.cover_url ? (
              <img src={fileUrl(profile.cover_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <ImageIcon size={28} strokeWidth={1.2} />
                <p className="text-xs">Click to upload a cover image (16:9, max 8 MB)</p>
              </div>
            )}
            {uploadingCover && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                <Loader2 className="animate-spin" />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadCover(e.target.files?.[0])}
            />
            <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.2em] bg-background/85 backdrop-blur px-2 py-1 border border-border opacity-0 group-hover:opacity-100 transition-opacity">
              Change
            </div>
          </label>
        </div>
      )}

      {/* Form */}
      <form
        onSubmit={applied ? submitEdit : submitApply}
        className="mt-10 space-y-6"
        data-testid="stylist-application-form"
      >
        <FormRow label="Display name" required>
          <Input
            data-testid="studio-display-name"
            required
            value={form.display_name}
            onChange={(e) => setF("display_name", e.target.value)}
            className="rounded-none h-11"
            placeholder="e.g., Anaïra Kapoor"
            maxLength={80}
          />
        </FormRow>

        <FormRow label="Headline" hint="Short tagline shown on the marketplace card (max 120 chars)">
          <Input
            data-testid="studio-headline"
            value={form.headline || ""}
            onChange={(e) => setF("headline", e.target.value)}
            className="rounded-none h-11"
            placeholder="e.g., Menswear · Office · Wedding"
            maxLength={120}
          />
        </FormRow>

        <FormRow label="Bio" required hint="What clients should know. Up to 1200 characters.">
          <Textarea
            data-testid="studio-bio"
            required
            value={form.bio}
            onChange={(e) => setF("bio", e.target.value)}
            rows={5}
            className="rounded-none"
            maxLength={1200}
            placeholder="Your journey, aesthetic, notable clients, awards…"
          />
        </FormRow>

        <FormRow label="Specialties" required hint="Pick up to 8. This drives AI Style Match.">
          <div className="flex flex-wrap gap-2" data-testid="studio-specialties">
            {SPECIALTY_KEYS.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`studio-spec-${s}`}
                onClick={() => toggleSpec(s)}
                className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors ${
                  form.specialties.includes(s)
                    ? "bg-accent text-accent-foreground border-accent"
                    : "border-border hover:border-foreground"
                }`}
              >
                {SPECIALTY_LABELS[s]}
              </button>
            ))}
          </div>
        </FormRow>

        <div className="grid md:grid-cols-2 gap-6">
          <FormRow label="City" required>
            <Input
              data-testid="studio-city"
              required
              value={form.city}
              onChange={(e) => setF("city", e.target.value)}
              className="rounded-none h-11"
              placeholder="Mumbai"
              maxLength={60}
            />
          </FormRow>
          <FormRow label="Years of experience">
            <Input
              type="number"
              data-testid="studio-experience"
              min={0} max={60}
              value={form.experience_years}
              onChange={(e) => setF("experience_years", e.target.value)}
              className="rounded-none h-11"
            />
          </FormRow>
          <FormRow label="Languages" hint="Comma separated (e.g., English, Hindi)">
            <Input
              data-testid="studio-languages"
              value={Array.isArray(form.languages) ? form.languages.join(", ") : form.languages}
              onChange={(e) => setF("languages", e.target.value)}
              className="rounded-none h-11"
              placeholder="English, Hindi"
            />
          </FormRow>
          <FormRow label="Serves">
            <select
              data-testid="studio-target-gender"
              value={form.target_gender}
              onChange={(e) => setF("target_gender", e.target.value)}
              className="w-full h-11 border border-input bg-background px-3 text-sm"
            >
              {TARGET_GENDERS.map((g) => (
                <option key={g} value={g}>{g === "all" ? "Everyone" : g === "male" ? "Men" : "Women"}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Price tier">
            <select
              data-testid="studio-price-tier"
              value={form.price_tier}
              onChange={(e) => setF("price_tier", e.target.value)}
              className="w-full h-11 border border-input bg-background px-3 text-sm"
            >
              {PRICE_TIERS.map((t) => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Starting rate (₹)">
            <Input
              type="number"
              data-testid="studio-hourly-rate"
              min={0}
              value={form.hourly_rate_inr}
              onChange={(e) => setF("hourly_rate_inr", e.target.value)}
              className="rounded-none h-11"
              placeholder="3500"
            />
          </FormRow>
          <FormRow label="Instagram handle">
            <Input
              data-testid="studio-instagram"
              value={form.instagram || ""}
              onChange={(e) => setF("instagram", e.target.value)}
              className="rounded-none h-11"
              placeholder="anaira.styles"
              maxLength={60}
            />
          </FormRow>
          <FormRow label="Portfolio URL">
            <Input
              type="url"
              data-testid="studio-portfolio-url"
              value={form.portfolio_url || ""}
              onChange={(e) => setF("portfolio_url", e.target.value)}
              className="rounded-none h-11"
              placeholder="https://…"
              maxLength={200}
            />
          </FormRow>
        </div>

        <div className="pt-4 flex items-center gap-3">
          <Button
            type="submit"
            data-testid="studio-submit-btn"
            disabled={saving}
            className="rounded-none h-12 px-8 uppercase tracking-[0.2em] text-[10px] gap-2"
          >
            {saving && <Loader2 className="animate-spin" size={14} />}
            {applied ? "Save changes" : "Submit application"}
            {!saving && <ArrowRight size={12} />}
          </Button>
          {!applied && (
            <p className="text-xs text-muted-foreground">By submitting, you agree to Stylebay's stylist Terms.</p>
          )}
        </div>
      </form>

      {applied && !isApproved && (
        <div className="mt-16 border border-dashed border-border p-6">
          <p className="eyebrow flex items-center gap-2"><ArrowLeft size={12} /> Portfolio · Bookings · Earnings</p>
          <p className="text-sm text-muted-foreground mt-3">
            These dashboards unlock the moment you're approved.
          </p>
        </div>
      )}

      {isApproved && (
        <div className="mt-12" data-testid="studio-approved-tabs">
          <div className="border-b border-border flex gap-1 overflow-x-auto -mx-6 lg:-mx-10 px-6 lg:px-10">
            {[
              { k: "profile", label: "Profile" },
              { k: "portfolio", label: "Portfolio" },
              { k: "services", label: "Services" },
              { k: "bookings", label: "Bookings" },
            ].map((t) => (
              <button
                key={t.k}
                data-testid={`studio-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`text-[11px] uppercase tracking-[0.2em] px-4 py-3 border-b-2 -mb-px transition-colors ${
                  tab === t.k ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mt-8">
            {tab === "portfolio" && <PortfolioTab />}
            {tab === "services" && <ServicesTab />}
            {tab === "bookings" && <BookingsInboxTab />}
            {tab === "profile" && (
              <p className="text-sm text-muted-foreground">Edit your profile details in the form above. Save to apply changes.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FormRow({ label, required, hint, children }) {
  return (
    <label className="block">
      <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
        {label}{required && <span className="text-destructive"> *</span>}
      </p>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground mt-1.5">{hint}</p>}
    </label>
  );
}

function PlaceholderTile({ title, body }) {
  return (
    <div className="border border-border p-5 bg-muted/20">
      <p className="font-serif text-xl">{title}</p>
      <p className="text-xs text-muted-foreground mt-2">{body}</p>
    </div>
  );
}

function profileToForm(p) {
  return {
    display_name: p.display_name || "",
    headline: p.headline || "",
    bio: p.bio || "",
    specialties: p.specialties || [],
    experience_years: p.experience_years || 0,
    languages: p.languages || [],
    city: p.city || "",
    target_gender: p.target_gender || "all",
    price_tier: p.price_tier || "premium",
    hourly_rate_inr: p.hourly_rate_inr || "",
    instagram: p.instagram || "",
    portfolio_url: p.portfolio_url || "",
  };
}

function validate(f) {
  if (!f.display_name.trim()) return notify("Display name is required.");
  if (!f.bio.trim() || f.bio.trim().length < 20) return notify("Bio needs at least 20 characters.");
  if (!f.city.trim()) return notify("City is required.");
  if (!f.specialties.length) return notify("Pick at least one specialty.");
  return true;
}

function notify(msg) { toast.error(msg); return false; }

function errMsg(e, fallback) {
  const raw = e?.response?.data?.detail;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object") return raw.message || raw.error || fallback;
  return e?.message || fallback;
}
